import React, { useState, useEffect, useRef } from "react";
import { api } from "../../lib/apiClient";
import { Download, Plus, Trash2, Upload } from "lucide-react";
import { Input, SimpleDialog } from "./_shared";
import { useConfirm } from "../../components/ConfirmDialog";

export function PostcodesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [importInfo, setImportInfo] = useState("");
  const fileRef = useRef();
  const confirm = useConfirm();
  const load = () => api.get("/postcodes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const newOne = () => setEditing({ code: "", depot: "Plymouth", travel_time_hours: 1 });
  const save = async () => { await api.post("/postcodes", editing); setEditing(null); load(); };
  const remove = async (id) => {
    const ok = await confirm({ title: "Delete this postcode?", confirmText: "Delete", destructive: true });
    if (!ok) return;
    await api.delete(`/postcodes/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/postcodes/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportInfo(`Imported · ${data.inserted} new, ${data.updated} updated${data.errors?.length ? `, ${data.errors.length} errors` : ""}`);
    } catch (err) {
      setImportInfo(err?.response?.data?.detail || "Import failed");
    }
    e.target.value = "";
    load();
  };

  const downloadTemplate = () => {
    const csv = "code,depot,travel_time_hours\nPL1,Plymouth,0.5\nBS1,Bristol,0.5\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "postcodes-template.csv";
    a.click();
  };

  const downloadList = () => {
    const header = "code,depot,travel_time_hours\n";
    const rows = items.map((p) => `${p.code},${p.depot},${p.travel_time_hours}`).join("\n");
    const blob = new Blob([header + rows + "\n"], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "postcodes.csv";
    a.click();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={newOne} className="h-9 px-4 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark" data-testid="new-postcode-button">
          <Plus size={14} /> Add Postcode
        </button>
        <button onClick={() => fileRef.current?.click()} className="h-9 px-4 border border-slate-900 hover:bg-slate-50 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="postcodes-import-button">
          <Upload size={14} /> Import CSV
        </button>
        <button onClick={downloadList} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="postcodes-download-button">
          <Download size={14} /> Download CSV
        </button>
        <button onClick={downloadTemplate} className="h-9 px-4 border border-slate-300 hover:border-slate-900 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2" data-testid="postcodes-template-button">
          <Download size={14} /> Template
        </button>
        <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" />
        {importInfo && <span className="text-xs text-slate-500 ml-2">{importInfo}</span>}
      </div>
      <div className="border border-slate-200 max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0"><tr className="bg-slate-50 border-b-2 border-slate-900"><th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Code</th><th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Depot</th><th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-600">Travel Time (hrs)</th><th></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="px-3 py-2 font-mono">{p.code}</td>
                <td className="px-3 py-2">{p.depot}</td>
                <td className="px-3 py-2 font-mono">{p.travel_time_hours}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => remove(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && (
        <SimpleDialog title="Add / Update Postcode" onClose={() => setEditing(null)} onSave={save}>
          <Input label="Code" value={editing.code} set={(v) => setEditing({ ...editing, code: v })} />
          <Input label="Depot" value={editing.depot} set={(v) => setEditing({ ...editing, depot: v })} />
          <Input label="Travel Time (hrs)" type="number" value={editing.travel_time_hours} set={(v) => setEditing({ ...editing, travel_time_hours: Number(v) })} />
        </SimpleDialog>
      )}
    </div>
  );
}
